import isNil from 'lodash/isNil';

/**
 * The jackpot pot, printed in full with thousands separators ("1,205,000") and never K/M abbreviated.
 *
 * Deliberately NOT `formatAmountByCoinType`, which abbreviates the GC/CC side to "487.15M": the number
 * under the crown is the advert, and the whole figure is the point.
 *
 * ## Two decimals, or none
 * An integer pot prints as an integer; a fractional one prints to exactly 2dp, rounded. A bare
 * `toLocaleString()` — which is what both widgets used to call — follows neither rule: it prints up to
 * THREE fraction digits and drops trailing zeros, so a pot ticking through 487,154,146.5 then
 * 487,154,146.567 rendered as `.5` then `.567`, changing width as it moved.
 *
 * This is game-ui's `formatJackpotAmount` (`src/modules/jackpot/formatJackpotAmount.ts`), reproduced
 * because the two repos share no code. The popup's ladder, the in-slot banner and the lobby widgets are
 * the same pot seen from three surfaces, and they have to agree digit for digit.
 *
 * Anything unparseable passes through untouched rather than rendering as "NaN".
 */
export function formatJackpotAmount(amount: string | number | null | undefined): string {
  if (isNil(amount) || amount === '') {
    return '';
  }

  const value = typeof amount === 'number' ? amount : Number(amount);

  if (!Number.isFinite(value)) {
    return String(amount);
  }

  return Number.isInteger(value)
    ? value.toLocaleString()
    : Number(value.toFixed(2)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const ROUNDING_TIERS = [
  { threshold: 1_000_000, suffix: 'M' },
  { threshold: 1_000, suffix: 'K' },
] as const;

/**
 * The pot as the LOBBY prints it: thousands as K, millions as M, one decimal at most and rounded —
 * "35.7K", "1K" (never "1.0K"). M is deliberately the top tier, so 2,500,000,000 reads "2500M", not
 * "2.5B". Every locale file spells `numbers.thousand`/`numbers.million` as K/M, so the suffixes are
 * literal here like the rest of this file.
 *
 * Anything under a thousand — and every nil / unparseable input — defers to
 * {@link formatJackpotAmount}: those figures still match the popup digit for digit, which now only
 * abbreviated pots do not.
 */
export function formatJackpotAmountRounded(amount: string | number | null | undefined): string {
  const value = typeof amount === 'number' ? amount : Number(amount);

  if (isNil(amount) || amount === '' || !Number.isFinite(value)) {
    return formatJackpotAmount(amount);
  }

  const tier = ROUNDING_TIERS.find(({ threshold }) => value >= threshold);

  if (!tier) {
    return formatJackpotAmount(amount);
  }

  const scaled = Math.round((value / tier.threshold) * 10) / 10;

  // 999,950+ rounds K-scaled to 1000 — a pot one rounding away from the next tier reads in that tier.
  return tier.suffix === 'K' && scaled >= 1000 ? '1M' : `${scaled}${tier.suffix}`;
}
